import { useTranslation } from "react-i18next";
import { useWebsiteContent } from "@/contexts/WebsiteContentContext";
import { usePreview } from "@/contexts/PreviewContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Eye, Rocket, Target } from "lucide-react";

const AboutArabookverse = () => {
  const { t } = useTranslation();
  const { content, isLoading } = useWebsiteContent();
  const { previewContent, isPreviewMode } = usePreview();
  const { language } = useLanguage();
  const lang = (language.startsWith('en') ? 'en' : 'ar') as 'en' | 'ar';

  const activeContent = (isPreviewMode && previewContent) ? previewContent : content;

  if (isLoading) {
    return (
      <section id="about-arabookverse" className="py-24 bg-muted/30 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary"></div>
      </section>
    );
  }

  const aboutArabookverse = activeContent?.aboutArabookverse;

  if (!aboutArabookverse) {
    return (
      <section id="about-arabookverse" className="py-24 bg-muted/30">
        <div className="container px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto text-center">
          <p className="text-muted-foreground">{t("aboutArabookverse.fallback")}</p>
        </div>
      </section>
    );
  }

  return (
    <section id="about-arabookverse" className="py-24 bg-muted/30" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
      <div className="container px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
        {/* Title */}
        <h2 className="text-4xl sm:text-5xl font-bold text-foreground mb-8 text-center">
          {aboutArabookverse.title?.[lang] || 'About Arabookverse'}
        </h2>

        {/* Main Description */}
        <p className="text-lg text-foreground/70 leading-relaxed max-w-4xl mx-auto text-center mb-16">
          {aboutArabookverse.description?.[lang]}
        </p>

        {/* Cards Grid: Vision, Mission, Purpose */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Vision Card */}
          <div className="bg-white dark:bg-card rounded-2xl p-8 shadow-sm border border-border/50 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 text-primary mb-6">
              <Eye className="h-7 w-7" />
            </div>
            <h3 className="text-xl font-semibold text-foreground mb-4">
              {aboutArabookverse.vision?.title?.[lang] || 'Our Vision'}
            </h3>
            <p className="text-foreground/70 leading-relaxed">
              {aboutArabookverse.vision?.description?.[lang]}
            </p>
          </div>

          {/* Mission Card */}
          <div className="bg-white dark:bg-card rounded-2xl p-8 shadow-sm border border-border/50 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 text-primary mb-6">
              <Rocket className="h-7 w-7" />
            </div>
            <h3 className="text-xl font-semibold text-foreground mb-4">
              {aboutArabookverse.mission?.title?.[lang] || 'Our Mission'}
            </h3>
            <p className="text-foreground/70 leading-relaxed">
              {aboutArabookverse.mission?.description?.[lang]}
            </p>
          </div>

          {/* Purpose Card */}
          <div className="bg-white dark:bg-card rounded-2xl p-8 shadow-sm border border-border/50 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 text-primary mb-6">
              <Target className="h-7 w-7" />
            </div>
            <h3 className="text-xl font-semibold text-foreground mb-4">
              {aboutArabookverse.purpose?.title?.[lang] || 'Our Purpose'}
            </h3>
            <ul className={`text-foreground/70 leading-relaxed space-y-2 ${lang === 'ar' ? 'text-right' : 'text-left'}`}>
              {aboutArabookverse.purpose?.items?.map((item, index) => (
                <li key={index} className="flex items-start gap-2">
                  <span className="text-primary mt-1 shrink-0">&#8226;</span>
                  <span>{item?.[lang]}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutArabookverse;
